import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { MandateRow, PqcCommitment, RegulatoryMilestoneRow, ReportingProfile, ReportingProfileView, VendorCommitmentRow } from "../api/remediation";
import { useAuth } from "../auth/AuthContext";
import { DESIGNATIONS, ORG_SECTORS, jurisdictionLabel, sectorLabel } from "../lib/mandateLabels";
import { statusTag } from "./cockpit/charts";

/**
 * Reporting profile (Sprint 5): what the organisation is regulated as, the project's planning year,
 * when the programme starts, and each vendor's post-quantum commitment. These drive the time tests,
 * the regulatory deadline test and the decisions the reports ask for. Applied from the next run.
 *
 *   Where it operates  jurisdictions (default: the company's country, plus the EU for member states)
 *   What it is         sectors (default: from the company's industry) and designations
 *   Deadline basis     binding instruments only, or binding and guidance
 *   Planning year      one year for every asset (Step 3): the first expert-curve year whose central estimate
 *                      reaches a tolerance (1 in 5 / 1 in 3 / 1 in 2), or a fixed year (2026-2060). The old
 *                      per-sensitivity risk appetite is no longer offered; saved values are ignored.
 *   Programme start    when migration work starts for assets without their own start date
 *   Prepared for       name and role printed on the Risk Assessment Report covers (optional)
 *   Vendors            PQC commitment per vendor (none / verbal / written roadmap with a date / delivered)
 */

const TOLERANCES: Array<{ pct: number; label: string; note: string }> = [
  { pct: 20, label: "1 in 5", note: "Cautious: plans for an earlier CRQC" },
  { pct: 33.3, label: "1 in 3", note: "Recommended default" },
  { pct: 50, label: "1 in 2", note: "The expert median" },
];
const COMMITMENT: Array<{ id: PqcCommitment; label: string }> = [
  { id: "none", label: "None" }, { id: "verbal", label: "Verbal only" }, { id: "written_roadmap", label: "Written roadmap" }, { id: "delivered", label: "Delivered" },
];

type Mode = "curve" | "year";

export function ReportingProfileSettings({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const auth = useAuth();
  const canEdit = auth.hasRole("admin", "super_user", "analyst");
  const profile = useQuery({ queryKey: ["reporting-profile", projectId], queryFn: () => api.get<ReportingProfileView>(`/projects/${projectId}/reporting-profile`) });
  const catalogue = useQuery({ queryKey: ["regulatory-milestones"], queryFn: () => api.get<RegulatoryMilestoneRow[]>(`/regulatory-milestones`) });
  const mandates = useQuery({ queryKey: ["mandates", projectId], queryFn: () => api.get<MandateRow[]>(`/projects/${projectId}/mandates`) });

  const [jurisdictions, setJurisdictions] = useState<string[] | null>(null);
  const [sectors, setSectors] = useState<string[] | null>(null);
  const [designations, setDesignations] = useState<string[]>([]);
  const [basis, setBasis] = useState<ReportingProfile["deadline_basis"]>("binding_and_guidance");
  const [mode, setMode] = useState<Mode>("curve");
  const [tolerance, setTolerance] = useState(33.3);
  const [horizon, setHorizon] = useState("");
  const [start, setStart] = useState("");
  const [currency, setCurrency] = useState("");
  const [preparedFor, setPreparedFor] = useState("");

  useEffect(() => {
    const p = profile.data;
    if (!p) return;
    setJurisdictions(p.jurisdictions);
    setSectors(p.sectors);
    setDesignations(p.designations ?? []);
    setBasis(p.deadline_basis ?? "binding_and_guidance");
    setMode(p.planning_year_mode === "fixed" ? "year" : "curve");
    setTolerance(Number(p.planning_tolerance_pct ?? 33.3));
    setHorizon(p.planning_horizon_year == null ? "" : String(p.planning_horizon_year));
    setStart(p.programme_start_date ?? "");
    setCurrency(p.currency ?? "");
    setPreparedFor(p.prepared_for ?? "");
  }, [profile.data]);

  const catalogueJurisdictions = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of catalogue.data ?? []) if (r.jurisdiction !== "INTL" && r.jurisdiction !== "US-NSS") m.set(r.jurisdiction, (m.get(r.jurisdiction) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => jurisdictionLabel(a[0]).localeCompare(jurisdictionLabel(b[0])));
  }, [catalogue.data]);

  const save = useMutation({
    mutationFn: () => api.put<ReportingProfileView>(`/projects/${projectId}/reporting-profile`, {
      jurisdictions: jurisdictions && jurisdictions.length > 0 ? jurisdictions : null,
      sectors: sectors && sectors.length > 0 ? sectors : null,
      designations: designations.length > 0 ? designations : null,
      deadline_basis: basis,
      planning_year_mode: mode === "year" ? "fixed" : "curve",
      planning_tolerance_pct: tolerance,
      planning_horizon_year: mode === "year" && horizon ? Number(horizon) : null,
      programme_start_date: start || null,
      currency: currency ? currency.toUpperCase() : null,
      prepared_for: preparedFor.trim() || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reporting-profile", projectId] });
      qc.invalidateQueries({ queryKey: ["mandates", projectId] });
    },
  });

  // An edit after a save makes the profile unsaved again.
  const { isSuccess: saved, reset: resetSave } = save;
  const formKey = JSON.stringify([jurisdictions, sectors, designations, basis, mode, tolerance, horizon, start, currency, preparedFor]);
  const savedKey = useRef<string | null>(null);
  useEffect(() => {
    if (saved && savedKey.current === null) savedKey.current = formKey;
    if (!saved) savedKey.current = null;
    if (saved && savedKey.current !== formKey) resetSave();
  }, [formKey, saved, resetSave]);

  const toggle = (list: string[] | null, v: string) => (list ?? []).includes(v) ? (list ?? []).filter((x) => x !== v) : [...(list ?? []), v];
  const currencyOk = currency === "" || /^[A-Za-z]{3}$/.test(currency);
  const horizonOk = mode === "curve" || (/^\d{4}$/.test(horizon) && Number(horizon) >= 2026 && Number(horizon) <= 2060);
  const eff = profile.data?.effective;
  const org = profile.data?.organisation;
  // The reports print the company record's name; an administrator corrects it here (every project of the company, from its next run).
  const [newName, setNewName] = useState<string | null>(null);
  const rename = useMutation({
    mutationFn: (name: string) => api.patch(`/companies/${org!.company_id}`, { name }),
    onSuccess: () => { setNewName(null); qc.invalidateQueries({ queryKey: ["reporting-profile", projectId] }); qc.invalidateQueries({ queryKey: ["companies"] }); },
  });
  const optionYear = (pct: number) => eff?.planning_year_options.find((o) => Math.abs(o.tolerance_pct - pct) < 0.05)?.year ?? null;
  const previewYear = mode === "year" ? (/^\d{4}$/.test(horizon) ? Number(horizon) : null) : (optionYear(tolerance) ?? (eff && eff.planning_year_options.every((o) => o.year === null) ? 2035 : null));

  return (
    <section className="mt-8 pt-6 border-t border-ink-100 space-y-6" data-testid="reporting-profile">
      <div>
        <h3 className="text-sm font-medium text-ink-900">Reporting profile</h3>
        <p className="text-xs text-ink-500 mt-1">What the organisation is regulated as, the planning year for a cryptographically relevant quantum computer (CRQC) and when migration starts. These set the time tests, the regulatory deadline test and the decisions in the reports. Applied from the next assessment run.</p>
      </div>

      {/* Settings are split into five sub-sections (C-050); layout only, one Save for 1-3 and 5. */}
      {/* 1. Organisation & designations */}
      <fieldset className="space-y-2" data-testid="profile-organisation">
        <legend className="text-xs font-semibold text-ink-800 uppercase tracking-wider">1. Organisation &amp; designations</legend>
        <p className="text-[11px] text-ink-500">
          Company record:{" "}
          {newName === null ? (
            <>
              <strong className="text-ink-700">{org?.company_name ?? "not linked"}</strong>
              {org?.company_id && auth.hasRole("admin") && (
                <button type="button" className="ml-1 text-accent-700 hover:underline" onClick={() => setNewName(org.company_name ?? "")} data-testid="company-rename">Rename</button>
              )}
            </>
          ) : (
            <span className="inline-flex items-center gap-1">
              <input aria-label="Company name" className="border border-ink-200 rounded px-1 py-0.5 text-xs w-72" value={newName} onChange={(e) => setNewName(e.target.value)} />
              <button type="button" className="text-accent-700 hover:underline disabled:opacity-50" disabled={!newName.trim() || rename.isPending} onClick={() => rename.mutate(newName.trim())} data-testid="company-rename-save">Save</button>
              <button type="button" className="text-ink-500 hover:underline" onClick={() => setNewName(null)}>Cancel</button>
              <span className="text-ink-400">Every project of this company, from its next assessment run.</span>
              {rename.isError && <span className="text-red-600">{(rename.error as Error).message}</span>}
            </span>
          )}
          {org?.country && ` · ${org.country}`}{org?.industry_type && ` · ${org.industry_type}`}.
          {eff && <> Assessed for <strong className="text-ink-700">{eff.jurisdictions.map(jurisdictionLabel).join(", ") || "no jurisdiction"}</strong> ({eff.jurisdictions_source === "profile" ? "set here" : eff.jurisdictions_source === "company_country" ? "from the company's country" : "unknown"}) as <strong className="text-ink-700">{eff.sectors.map(sectorLabel).join(", ") || "no sector"}</strong> ({eff.sectors_source === "profile" ? "set here" : eff.sectors_source === "company_industry" ? "from the company's industry" : "unknown"}).</>}
        </p>
        <div>
          <label className="flex items-center gap-2 text-xs text-ink-700">
            <input type="checkbox" disabled={!canEdit} checked={sectors === null} onChange={(e) => setSectors(e.target.checked ? null : (eff?.sectors ?? []).filter((x) => (ORG_SECTORS as readonly string[]).includes(x)))} />
            Use the company's industry for sectors
          </label>
          {sectors !== null && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 pl-5" data-testid="sector-list">
              {ORG_SECTORS.map((sct) => (
                <label key={sct} className="flex items-center gap-2 text-xs"><input type="checkbox" disabled={!canEdit} checked={sectors.includes(sct)} onChange={() => setSectors(toggle(sectors, sct))} />{sectorLabel(sct)}</label>
              ))}
            </div>
          )}
        </div>
        <div className="border border-ink-100 rounded-sm px-3 py-2 space-y-1.5" data-testid="designation-list">
          <div className="text-xs font-semibold text-ink-800">Designations</div>
          <p className="text-[11px] text-ink-500">A designation can put the organisation on a different deadline track of the same instrument. Tick only what a regulator or government has formally designated.</p>
          {DESIGNATIONS.map((d) => {
            const keyed = [...new Set((mandates.data ?? []).filter((r) => r.sectors.includes(d)).map((r) => r.short_title ?? r.framework))];
            return (
              <div key={d}>
                <label className="flex items-center gap-2 text-xs text-ink-800" data-testid={`designation-${d}`}>
                  <input type="checkbox" disabled={!canEdit} checked={designations.includes(d)} onChange={() => setDesignations(toggle(designations, d) ?? [])} />
                  {d === "critical_infrastructure" ? "Critical information infrastructure (CII)" : sectorLabel(d)}
                </label>
                <p className="text-[11px] text-ink-500 pl-5" data-testid={`designation-help-${d}`}>
                  {DESIGNATION_HELP[d] ?? "Brings in catalogue instruments addressed to this designation."}
                  {keyed.length > 0 && <> Catalogue rows keyed on it: {keyed.join("; ")}.</>}
                </p>
              </div>
            );
          })}
        </div>
      </fieldset>

      {/* 2. Planning year (Step 3) */}
      <fieldset className="space-y-2" data-testid="profile-planning-year">
        <legend className="text-xs font-semibold text-ink-800 uppercase tracking-wider">2. CRQC planning year</legend>
        <p className="text-[11px] text-ink-500">The one year this project plans for a CRQC. The Timeline dimension and every asset's time tests use it: an asset is out of time when it cannot finish migrating (operational window), or its data or signatures cannot stop needing protection (lifetime), before 1 January of the planning year.</p>
        <label className="flex items-center gap-2 text-xs"><input type="radio" name="mode" disabled={!canEdit} checked={mode === "curve"} onChange={() => setMode("curve")} />Expert curve at a tolerance</label>
        {mode === "curve" && (
          <div className="pl-5 space-y-1" data-testid="planning-tolerance">
            {TOLERANCES.map((t) => (
              <label key={t.pct} className="flex items-baseline gap-2 text-xs">
                <input type="radio" name="tolerance" disabled={!canEdit} checked={Math.abs(tolerance - t.pct) < 0.05} onChange={() => setTolerance(t.pct)} />
                <span className="text-ink-800">{t.label}</span>
                <span className="tabular-nums text-ink-700">{optionYear(t.pct) ?? "—"}</span>
                <span className="text-[11px] text-ink-400">{t.note}</span>
              </label>
            ))}
            <p className="text-[11px] text-ink-500">The first year in which the expert survey's central estimate gives at least this chance of a CRQC. If the curve is empty, 2035 is used.</p>
          </div>
        )}
        <label className="flex items-center gap-2 text-xs"><input type="radio" name="mode" disabled={!canEdit} checked={mode === "year"} onChange={() => setMode("year")} />Fixed year</label>
        {mode === "year" && (
          <label className="block pl-5 text-xs text-ink-600">CRQC planning year
            <input type="number" min={new Date().getUTCFullYear() + 1} max={2060} disabled={!canEdit} value={horizon} onChange={(e) => setHorizon(e.target.value)} placeholder="2035" className="block w-32 mt-1" />
            {!horizonOk && <span className="block text-xs text-accent mt-1">Use a year between 2026 and 2060.</span>}
          </label>
        )}
        <p className="text-xs text-ink-700" data-testid="planning-year-preview">CRQC planning year for the next run: <strong className="tabular-nums">{previewYear ?? "—"}</strong></p>
        <div data-testid="profile-programme">
        <label className="block text-xs text-ink-600">Programme start date
          <input type="date" disabled={!canEdit} value={start} onChange={(e) => setStart(e.target.value)} className="block mt-1" />
          <span className="block text-[11px] text-ink-500 mt-1 max-w-xs">Used for assets without their own migration start date. Empty means the work is assumed to start on the run date, and the reports say so.</span>
        </label>
        </div>
      </fieldset>

      {/* 3. Deadlines and jurisdictions */}
      <fieldset className="space-y-2" data-testid="profile-deadlines">
        <legend className="text-xs font-semibold text-ink-800 uppercase tracking-wider">3. Deadlines and jurisdictions</legend>
        <p className="text-[11px] text-ink-500">Where the organisation is regulated and which instruments count when testing each asset against its regulatory deadline.</p>
        <div>
          <label className="flex items-center gap-2 text-xs text-ink-700">
            <input type="checkbox" disabled={!canEdit} checked={jurisdictions === null} onChange={(e) => setJurisdictions(e.target.checked ? null : (eff?.jurisdictions.filter((j) => j !== "INTL") ?? []))} />
            Use the company's country for jurisdictions
          </label>
          {jurisdictions !== null && (
            <div className="grid grid-cols-3 gap-x-4 gap-y-1 mt-1.5 pl-5" data-testid="jurisdiction-list">
              {catalogueJurisdictions.map(([j, n]) => (
                <label key={j} className="flex items-center gap-2 text-xs">
                  <input type="checkbox" disabled={!canEdit} checked={jurisdictions.includes(j)} onChange={() => setJurisdictions(toggle(jurisdictions, j))} />
                  <span className="text-ink-800">{jurisdictionLabel(j)}</span><span className="text-ink-400">{n}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          <span className="text-ink-700">Regulatory deadline from:</span>
          <label className="flex items-center gap-1.5"><input type="radio" name="basis" disabled={!canEdit} checked={basis === "binding_and_guidance"} onChange={() => setBasis("binding_and_guidance")} />binding instruments and guidance</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="basis" disabled={!canEdit} checked={basis === "binding_only"} onChange={() => setBasis("binding_only")} />binding instruments only</label>
        </div>
        {/* Applicable instruments, as the last save sees them */}
        <MandatesPreview rows={mandates.data} />
      </fieldset>

      {/* 4. Vendor commitments (each row saves on its own) */}
      <fieldset className="space-y-2" data-testid="profile-vendors">
        <legend className="text-xs font-semibold text-ink-800 uppercase tracking-wider">4. Vendor commitments</legend>
        <VendorCommitments projectId={projectId} canEdit={canEdit} />
      </fieldset>

      {/* 5. Currency and Prepared for */}
      <fieldset className="flex flex-wrap gap-6" data-testid="profile-currency">
        <legend className="text-xs font-semibold text-ink-800 uppercase tracking-wider" >5. Currency and Prepared for</legend>
        <label className="block text-xs text-ink-600 w-40">Cost currency (ISO 4217)
          <input type="text" maxLength={3} disabled={!canEdit} value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} placeholder="USD" className="block w-full mt-1 uppercase" />
          {!currencyOk && <span className="block text-xs text-accent mt-1">Use a three-letter code, e.g. USD, EUR, INR.</span>}
        </label>
        <label className="block text-xs text-ink-600 w-80">Prepared for (name and role, optional)
          <input type="text" maxLength={160} disabled={!canEdit} value={preparedFor} onChange={(e) => setPreparedFor(e.target.value)} placeholder="A. Sharma, Chief Information Security Officer" className="block w-full mt-1" data-testid="profile-prepared-for" />
          <span className="block text-[11px] text-ink-500 mt-1">Printed on the Risk Assessment Report covers under the organisation. Empty: the project owner. Applies to the next report, no re-run needed.</span>
          {preparedFor.includes("@") && <span className="block text-xs text-accent mt-1">Give a name and role, not an e-mail address.</span>}
        </label>
      </fieldset>

      {save.error && <p className="text-xs text-accent">{(save.error as Error).message}</p>}
      {canEdit && (
        <button type="button" className="btn-primary" disabled={!currencyOk || !horizonOk || preparedFor.includes("@") || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : save.isSuccess ? "Saved — re-run to apply" : "Save reporting profile"}
        </button>
      )}

    </section>
  );
}

/** One-line help per designation: what ticking it changes. */
const DESIGNATION_HELP: Record<string, string> = {
  critical_infrastructure: "Switches the India DST/NQM task force deadlines from the regular adopters track (2028 / 2030 / 2033) to the CII track (plan 2027, high-priority 2028, all 2029; draft-report guidance), and applies any other instrument addressed to critical infrastructure.",
  vendors_to_government: "Applies instruments addressed to suppliers of government (for example procurement and CBOM requirements).",
  national_security: "Applies instruments for operators or suppliers of national security systems (for example CNSA 2.0 in the United States).",
  banking: "Applies instruments addressed to entities licensed by a banking regulator (for example the RBI IT Governance Master Direction in India): banks, NBFCs and payment-system operators. A banking or lending industry implies it.",
  securities_market: "Applies instruments addressed to entities registered with a securities-market regulator (for example SEBI CSCRF in India). A capital-markets industry implies it; tick it for a bank with registered securities activities (depository participant, custodian, banker to an issue).",
};

function MandatesPreview({ rows }: { rows: MandateRow[] | undefined }) {
  if (!rows) return null;
  const order = ["obligation", "guidance", "reference", "other_sector"];
  const instruments = new Map<string, MandateRow[]>();
  for (const r of rows) {
    const key = `${r.jurisdiction}|${r.short_title ?? r.framework}|${r.sectors.join(",")}`;
    instruments.set(key, [...(instruments.get(key) ?? []), r]);
  }
  const list = [...instruments.values()].sort((a, b) => order.indexOf(a[0]!.applicability) - order.indexOf(b[0]!.applicability));
  return (
    <div data-testid="mandates-preview">
      <h4 className="text-xs font-semibold text-ink-800">Instruments that concern this organisation <span className="font-normal text-ink-400">(as saved)</span></h4>
      {list.length === 0 ? <p className="text-[11px] text-ink-500 mt-1">None in the catalogue.</p> : (
        <table className="w-full text-xs mt-1">
          <tbody>
            {list.map((ms) => {
              const r = ms[0]!;
              const tested = ms.find((m) => m.sets_deadline);
              const years = [...new Set(ms.filter((m) => m.milestone_date).map((m) => m.milestone_date!.slice(0, 4)))].sort();
              return (
                <tr key={`${r.jurisdiction}-${r.id}`} className="border-t border-ink-50 align-top">
                  <td className="py-1 pr-2 text-[10px] uppercase tracking-wide text-ink-500 whitespace-nowrap">{statusTag(r.applicability, r.binding)}</td>
                  <td className="py-1 pr-2">
                    <span className="text-ink-800">{r.short_title ?? r.framework}</span> <span className="text-ink-400">· {jurisdictionLabel(r.jurisdiction)}</span>{r.verification === "secondary" && <span className="text-ink-400"> · secondary source</span>}
                    {r.applies_to && <span className="text-[10.5px] text-ink-500 line-clamp-1" title={r.applies_to}>{r.applies_to}</span>}
                  </td>
                  <td className="py-1 text-right whitespace-nowrap text-ink-600">
                    {years.length === 0 ? "no date" : years.map((y, i) => {
                      const isTested = tested?.milestone_date?.startsWith(y);
                      return <span key={y}>{i > 0 && " · "}{isTested ? <strong className="text-ink-900" title="This milestone sets the regulatory deadline test">{y} (tested)</strong> : y}</span>;
                    })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function VendorCommitments({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["vendor-commitments", projectId], queryFn: () => api.get<VendorCommitmentRow[]>(`/projects/${projectId}/vendor-commitments`) });
  return (
    <div data-testid="vendor-commitments">
      <h4 className="text-xs font-semibold text-ink-800">Vendor post-quantum commitments</h4>
      <p className="text-[11px] text-ink-500 mt-0.5" data-testid="vendor-commitments-help">Used by the time test D (years before migration can start), one row per vendor named on the assets. The vendor itself is named per asset on the asset form (Supply chain); the optional Vendor readiness questionnaire (its own tab, when turned on under Optional modules) scores a supplier&apos;s answers into the assets&apos; Supply chain input. Each row saves on its own.</p>
      <p className="text-[11px] text-ink-500 mt-0.5">An asset whose vendor has made no commitment is out of time whatever the estimate: it cannot migrate until the vendor ships. A written roadmap date sets the earliest the asset can finish.</p>
      {q.data && q.data.length === 0 && <p className="text-[11px] text-ink-500 mt-1">No vendor-supplied assets in this project.</p>}
      {q.data && q.data.length > 0 && (
        <div className="overflow-x-auto"><table className="w-full text-xs mt-2">
          <thead><tr className="text-[10px] uppercase tracking-wider text-ink-500 text-left"><th className="py-1">Vendor</th><th className="py-1 text-right pr-3">Assets</th><th className="py-1">Commitment</th><th className="py-1">Committed date</th><th className="py-1">Contract renewal</th><th className="py-1">Notes</th><th className="py-1">Source URL</th><th /></tr></thead>
          <tbody>{q.data.map((v) => <VendorRowEditor key={`${v.vendor_name}|${v.pqc_commitment}|${v.committed_date}|${v.contract_renewal_date}|${v.notes}|${v.source_url}|${v.recorded}`} projectId={projectId} row={v} canEdit={canEdit} onSaved={() => qc.invalidateQueries({ queryKey: ["vendor-commitments", projectId] })} />)}</tbody>
        </table></div>
      )}
    </div>
  );
}

function VendorRowEditor({ projectId, row, canEdit, onSaved }: { projectId: string; row: VendorCommitmentRow; canEdit: boolean; onSaved: () => void }) {
  const [state, setState] = useState<PqcCommitment | "">(row.pqc_commitment ?? "");
  const [date, setDate] = useState(row.committed_date ?? "");
  const [renewal, setRenewal] = useState(row.contract_renewal_date ?? "");
  const [notes, setNotes] = useState(row.notes ?? "");
  const [source, setSource] = useState(row.source_url ?? "");
  const dirty = state !== (row.pqc_commitment ?? "") || date !== (row.committed_date ?? "") || renewal !== (row.contract_renewal_date ?? "") || notes !== (row.notes ?? "") || source !== (row.source_url ?? "");
  const needsDate = state === "written_roadmap" && !date;
  // Every commitment cites the document it is taken from (vendor release notes, roadmap, letter).
  // "None" may have no URL: nothing public was found, and the reports say "Not found".
  const needsSource = !!state && state !== "none" && !/^https?:\/\/\S+$/i.test(source.trim());
  const save = useMutation({
    mutationFn: () => api.put(`/projects/${projectId}/vendor-commitments`, {
      vendor_name: row.vendor_name, pqc_commitment: state || null, committed_date: date || null, contract_renewal_date: renewal || null, notes: notes || null,
      source_url: source.trim() || null,
    }),
    onSuccess: onSaved,
  });
  const clear = useMutation({ mutationFn: () => api.delete(`/projects/${projectId}/vendor-commitments/${encodeURIComponent(row.vendor_name)}`), onSuccess: onSaved });
  return (
    <tr className="border-t border-ink-50 align-top" data-testid={`vendor-${row.vendor_name}`}>
      <td className="py-1 pr-2 text-ink-800 whitespace-nowrap">{row.vendor_name}
        {row.pqc_commitment === "none" && !row.source_url && <span className="block text-[10px] text-ink-400">Not found: no public statement</span>}
        {row.origin === "knowledge_base" && <span className="block text-[10px] text-ink-400" title={row.verified_on ? `Checked ${row.verified_on}` : undefined}>From QRAM's vendor knowledge base</span>}</td>
      <td className="py-1 pr-3 text-right tabular-nums text-ink-600">{row.assets}</td>
      <td className="py-1 pr-2">
        <select aria-label={`Commitment for ${row.vendor_name}`} disabled={!canEdit} value={state} onChange={(e) => {
          const next = e.target.value as PqcCommitment | "";
          setState(next);
          // Only a written roadmap or a delivery carries a date; a verbal or absent commitment cannot be planned to.
          if (next !== "written_roadmap" && next !== "delivered") setDate("");
        }} className="text-xs">
          <option value="">Not recorded</option>
          {COMMITMENT.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </td>
      <td className="py-1 pr-2"><input type="date" aria-label={`Committed date for ${row.vendor_name}`} disabled={!canEdit || (state !== "written_roadmap" && state !== "delivered")} value={date} onChange={(e) => setDate(e.target.value)} className="text-xs" />{needsDate && <span className="block text-[10.5px] text-accent">A written roadmap needs its date</span>}</td>
      <td className="py-1 pr-2"><input type="date" aria-label={`Contract renewal for ${row.vendor_name}`} disabled={!canEdit} value={renewal} onChange={(e) => setRenewal(e.target.value)} className="text-xs" /></td>
      <td className="py-1 pr-2"><input type="text" aria-label={`Notes for ${row.vendor_name}`} maxLength={600} disabled={!canEdit} value={notes} onChange={(e) => setNotes(e.target.value)} className="text-xs w-full min-w-[160px]" /></td>
      <td className="py-1 pr-2">
        <input type="url" aria-label={`Source URL for ${row.vendor_name}`} placeholder={state === "none" ? "https://… (blank = not found)" : "https://… (required)"} maxLength={500} disabled={!canEdit} value={source} onChange={(e) => setSource(e.target.value)} className={`text-xs w-full min-w-[180px] ${needsSource ? "border-accent" : ""}`} />
        {row.source_url && /^https?:\/\//i.test(row.source_url) && <a href={row.source_url} target="_blank" rel="noopener noreferrer" className="block text-[10px] text-accent hover:underline truncate max-w-[220px]">Open source</a>}
      </td>
      <td className="py-1 whitespace-nowrap">
        {canEdit && <button type="button" className="text-xs text-accent hover:underline disabled:text-ink-300" disabled={!dirty || needsDate || needsSource || save.isPending} title={needsSource ? "Add the URL the commitment is taken from" : undefined} onClick={() => save.mutate()}>Save</button>}
        {canEdit && row.recorded && <button type="button" className="text-xs text-ink-500 hover:underline ml-2" onClick={() => clear.mutate()}>Clear</button>}
        {(save.error || clear.error) && <span className="block text-[10.5px] text-accent">{((save.error ?? clear.error) as Error).message}</span>}
      </td>
    </tr>
  );
}
